// Layer 3 (Transformation): pure projection for the Metallurgist stage ladder
// (paul-email.A2 #247, docs/logic/overview/met-stage-ladder.md). No React, no
// engine mutation — every cell it prints comes from `spcRead`.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SignalCell } from '@/versions/v1/schema/spc';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { formatSpcValue, inBandDivergences, spcRead, valueBandPosition } from '@/versions/v1/shared/SpcEngine';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { readingComparisonBasis } from '@/versions/v1/shared/comparison-basis';
import { CASES, VARIATION_EVENTS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { INITIAL_ADVISORY_STATE, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { applyCases } from '@/versions/v1/shared/ScenarioEngine';
import type { Case } from '@/versions/v1/schema/case';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';
import { matchedRuleIdForCase } from '@/versions/v1/shared/RuleSetEngine';
import { workbookTooltipForCauseIds } from '@/versions/v1/shared/met-diagnostics-cause.derive';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { plantNode, subprocessForNodeId } from '@/versions/v1/shared/bunker-hill-plant-nodes';
import { UNIT_KEY_READING_IDS } from '@/versions/v1/shared/bunker-hill-readings';
import type { ShiftWindow } from '@/versions/v1/shared/clock.derive';
import { windowContains } from '@/versions/v1/shared/roster-period.derive';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { CONTROL_LOOPS } from '@/versions/v1/shared/bunker-hill-seed';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';

export type SampleDivergenceChip = { direction: 'up' | 'down'; requested: boolean; requestedAt?: string; samplesHref: string };

export type StageId = 'grinding' | 'pb-rougher' | 'pb-cleaner' | 'zn-rougher' | 'zn-cleaner';

/**
 * The plant's five flotation stages, in flow order — the one authored
 * constant on the ladder (everything else is derived). `outcomeReadingId`
 * per stage per the logic doc: grinding output -> the grind P80 (the
 * circuit's product spec); Pb conditioner & rougher -> the rougher
 * concentrate Pb grade at Module 1 par8.2 SA06 (SA02 is the rougher *feed*,
 * not this outcome -- MS-1 build report decision 7, 2026-09-04); Pb regrind
 * & cleaner -> the final Pb concentrate grade; Zn conditioner & rougher ->
 * the rougher concentrate Zn grade; Zn cleaner -> the final Zn concentrate
 * grade.
 */
/**
 * `paul-call.A2` #252 (circuits stream, wave 3): a stage's readings grouped
 * one level finer, for the plant-circuits "open circuit" view
 * (`modules/ingestion/Circuits.transform.ts`) -- the ladder's own five rungs
 * and their rows are UNCHANGED (`StageLadder.tsx` reads `STAGES` /
 * `stageForReading` exactly as before; `substages` is additive and nothing
 * here reads it). Every reading a stage owns (per `stageForReading` /
 * `STAGE_OVERRIDES` below, PLUS the one reading named below that carries no
 * `TAG_MAPPING` row at all) lands in exactly one of its stage's substages --
 * asserted by `scripts/seed/circuits-probe.mjs`.
 */
export type Substage = {
  id: string;
  label: string;
  readingIds: readonly string[];
};

export const STAGES: readonly {
  id: StageId;
  label: string;
  subprocessId: string;
  unitOps: readonly string[];
  outcomeReadingId: string;
  substages: readonly Substage[];
}[] = [
  {
    id: 'grinding',
    label: 'Grinding output',
    subprocessId: 'sp-grind',
    unitOps: ['BM01', 'CY01'],
    outcomeReadingId: 'rd-spec-p80',
    substages: [
      {
        id: 'grinding-mill',
        label: 'Ball mill + cyclone pack',
        readingIds: [
          'rd-c-feed',
          'rd-jt023-kwpt',
          'rd-kpv-cyc-p',
          'rd-spec-p80',
          'rd-head-zn',
          'rd-head-pb',
          'rd-head-ag',
          'rd-head-as',
          'rd-head-sio2',
          'rd-feed-flow',
          'rd-co-dens',
          'rd-grind-ph',
        ],
      },
    ],
  },
  {
    id: 'pb-rougher',
    label: 'Pb conditioner and rougher',
    subprocessId: 'sp-pb',
    unitOps: ['PB01'],
    outcomeReadingId: 'rd-pb-rgh-con-pb',
    substages: [
      {
        id: 'pb-conditioner',
        label: 'Conditioner',
        readingIds: [
          'rd-kpv-pb-ph',
          'rd-pb-rgh-dens',
          'rd-reag-znso4',
          'rd-reag-smbs',
          'rd-reag-mibc',
          'rd-reag-3418a',
          'rd-reag-aero',
          'rd-reag-lime',
        ],
      },
      {
        id: 'pb-rougher-stage',
        label: 'Rougher',
        readingIds: ['rd-pb-rgh-air', 'rd-pb-rgh-level', 'rd-pb-rgh-froth', 'rd-pb-rgh-con-pb', 'rd-pb-rgh-con-zn'],
      },
    ],
  },
  {
    id: 'pb-cleaner',
    label: 'Pb regrind and cleaner',
    subprocessId: 'sp-pb',
    unitOps: ['RG01'],
    outcomeReadingId: 'rd-kpi-pb-grade',
    substages: [
      // `rd-unit-rg01-p80` (Reading, `bunker-hill-readings.ts`) carries no
      // `MonitoredVariable` / `TAG_MAPPING` row -- seed files are frozen for
      // this stream (circuits brief, Hard rules), so it can't be wired to
      // the SPC engine here; it renders `not-computed` honestly rather than
      // being invented a signal. Recorded as a deviation in the build report.
      { id: 'pb-regrind', label: 'Regrind', readingIds: ['rd-unit-rg01-p80'] },
      { id: 'pb-cleaners', label: 'Cleaners', readingIds: ['rd-kpi-pb-grade', 'rd-kpi-ag-grade', 'rd-pb-con-zn'] },
    ],
  },
  {
    id: 'zn-rougher',
    label: 'Zn conditioner and rougher',
    subprocessId: 'sp-zn',
    unitOps: ['ZN01'],
    outcomeReadingId: 'rd-zn-rgh-con-zn',
    substages: [
      {
        id: 'zn-conditioner-rougher',
        label: 'Conditioner & rougher',
        readingIds: [
          'rd-kpv-zn-ph',
          'rd-reag-cuso4',
          'rd-zn-rgh-dens',
          'rd-zn-rgh-air',
          'rd-zn-rgh-level',
          'rd-zn-rgh-froth',
          'rd-zn-rgh-con-zn',
        ],
      },
    ],
  },
  {
    id: 'zn-cleaner',
    label: 'Zn cleaner',
    subprocessId: 'sp-zn',
    unitOps: [],
    outcomeReadingId: 'rd-kpi-zn-grade',
    substages: [{ id: 'zn-cleaners', label: 'Cleaners', readingIds: ['rd-kpi-zn-grade'] }],
  },
];

/**
 * Override table (Q2) — readings the unit-op derivation cannot place, or
 * places wrong: the six reagents and the Pb-rougher pH loop read together
 * with the rest of the Pb conditioner & rougher stage even though their
 * reading ids carry no `UNIT_KEY_READING_IDS` entry; the CuSO4 activator and
 * the Zn float pH read with the Zn conditioner & rougher stage for the same
 * reason. `rd-pb-con-zn` sits under `PB01` in `UNIT_KEY_READING_IDS` (the
 * unit op groups conditioner/rougher AND regrind/cleaner readings under one
 * equipment id) but is a Pb regrind & cleaner read (MS-1 decision) — the
 * override wins over the unit-op match. The two final concentrate grades
 * (Pb, Zn) and the Ag grade beside them carry no unit-op reading id at all
 * and are placed by the same rule. WIT-021 / JT-023 / PI-1205 are grinding-
 * circuit instrument variables seeded before `UNIT_KEY_READING_IDS` existed
 * (their reading ids predate the BM01/CY01 lists); Grinding output is the
 * plant's only `sp-grind` stage, so they read there by elimination.
 */
const STAGE_OVERRIDES: Record<string, StageId> = {
  'rd-pb-con-zn': 'pb-cleaner',
  'rd-kpi-pb-grade': 'pb-cleaner',
  'rd-kpi-ag-grade': 'pb-cleaner',
  'rd-kpi-zn-grade': 'zn-cleaner',
  'rd-kpv-zn-ph': 'zn-rougher',
  'rd-reag-cuso4': 'zn-rougher',
  'rd-kpv-pb-ph': 'pb-rougher',
  'rd-reag-znso4': 'pb-rougher',
  'rd-reag-smbs': 'pb-rougher',
  'rd-reag-mibc': 'pb-rougher',
  'rd-reag-3418a': 'pb-rougher',
  'rd-reag-aero': 'pb-rougher',
  'rd-reag-lime': 'pb-rougher',
  'rd-c-feed': 'grinding',
  'rd-jt023-kwpt': 'grinding',
  'rd-kpv-cyc-p': 'grinding',
};

const TAG_BY_READING = new Map(TAG_MAPPING.map((row) => [row.readingId, row]));

/** Deck roll-up KPIs (mill utilisation, Pb/Zn/Ag recovery) — plant-wide daily
 * accounting figures, not a per-stage instrument read; they surface on the
 * KPI strip and the recovery trend, not the ladder (Q2: reported unfitted,
 * never force-fitted). */
const UNFITTED_READING_IDS = new Set(['rd-kpi-mill-util', 'rd-kpi-pb-rec', 'rd-kpi-zn-rec', 'rd-kpi-ag-rec']);

const CELL_RANK: Record<SignalCell | 'not-computed', number> = {
  consequence: 3,
  drift: 2,
  'not-capable': 1,
  quiet: 0,
  'not-computed': 0,
};

/** Which stage a monitored variable's reading belongs to — override first,
 * then unit-op derivation from `TAG_MAPPING.subprocessId` + `UNIT_KEY_READING_IDS`,
 * else `undefined` (unfitted). Exported so the probe can assert MS-1's
 * `STAGE_BY_MV` map agrees, row for row. */
export function stageForReading(readingId: string): StageId | undefined {
  const override = STAGE_OVERRIDES[readingId];
  if (override) return override;
  if (UNFITTED_READING_IDS.has(readingId)) return undefined;
  const tag = TAG_BY_READING.get(readingId);
  if (!tag) return undefined;
  for (const stage of STAGES) {
    if (stage.subprocessId !== tag.subprocessId) continue;
    for (const unitOp of stage.unitOps) {
      if ((UNIT_KEY_READING_IDS[unitOp] ?? []).includes(readingId)) return stage.id;
    }
  }
  return undefined;
}

/** The tag-level plant node for a reading (via `TAG_MAPPING` -> `tag-<role>`
 * in `bunker-hill-plant-nodes.ts`), normalised to the subprocess-level id the
 * ingestion Scene's `selectedNodeId` actually renders against — the Scene's
 * `nodeDetail` / `nodeSummary` are keyed on subprocess ids, so a deep link to
 * a finer-grained node is resolved up to its owning area on arrival (the
 * Scene's `?node=` reader does this resolution; recorded here per Q6). */
function nodeIdForReading(readingId: string): string {
  const tag = TAG_BY_READING.get(readingId);
  if (tag) {
    const tagNodeId = `tag-${tag.role}`;
    if (plantNode(tagNodeId)) return tagNodeId;
    return tag.subprocessId;
  }
  return 'bh-plant';
}

function nodeHref(slug: string, nodeId: string, readingId?: string): string {
  const base = `${projectSidebarHref(slug, 'plant-model')}?node=${nodeId}`;
  return readingId ? `${base}&reading=${readingId}` : base;
}

/** Constraint-row reading for deep link — only `rd-c-*` rows exist in the operating window. */
function constraintDeepLinkReading(readingId?: string): string | undefined {
  return readingId?.startsWith('rd-c-') ? readingId : undefined;
}

function windowReadingForStage(stage: (typeof STAGES)[number]): string | undefined {
  for (const substage of stage.substages) {
    for (const id of substage.readingIds) {
      if (id.startsWith('rd-c-')) return id;
    }
  }
  return undefined;
}

export type LadderCell = {
  readingId: string;
  variableId: string;
  label: string;
  /** Display string (`formatSpcValue`) — locale-formatted, not for arithmetic. */
  value: string;
  /** The numeric reading value the meter and any future arithmetic use — `value` is display-only. */
  raw: number;
  unit: string;
  bandPosition: 'inside' | 'below' | 'above' | 'n/a';
  band?: { low: number; high: number };
  basis?: { label: string; value: number };
  /** MS-F7: half-width of the reading's own tolerance (from `reading.spec`
   * when it is a closed band), for scaling a basis-only meter's deviation
   * window. `undefined` when the reading carries no spec band -- the caller
   * falls back to a fixed ±10% of the basis. */
  toleranceHalfWidth?: number;
  cell: SignalCell | 'not-computed' | 'no-series';
  since?: string;
  caseId?: string;
  caseLabel?: string;
  nodeHref: string;
  syn: boolean;
  sampleDivergence?: SampleDivergenceChip;
  /** Paul's workbook L/P figures — tooltip only (#299). */
  workbookTooltip?: string;
  /** polish.P26 adjudication 2: a reading with no point basis reads its
   *  Control Plan band as the comparison (`10.8–11.2`), and its meter is
   *  band-scaled. `undefined` when a point basis exists or no band does. */
  bandLabel?: string;
};

export type StageRow = {
  id: StageId;
  label: string;
  state: SignalCell | 'not-computed';
  outcome: LadderCell;
  rows: readonly LadderCell[];
  hidden: number;
  /** MS-F19: true when this stage's sole member reading IS its own outcome --
   * the header line already says everything, so `rows.length === 0` and
   * `hidden === 0` are the *intended* output, not an empty group. The probe
   * reads this flag to tell the two states apart. */
  soleOutcome: boolean;
  synCount: number;
  synTotal: number;
  nodeHref: string;
  since?: string;
};

export type StageLadderModel = {
  stages: readonly StageRow[];
  /** Reading ids whose monitored variable is `sp-grind` / `sp-pb` / `sp-zn`
   * scoped but fits no stage — reported, never silently dropped. */
  unfitted: readonly string[];
};

function raisedAtClock(at: string): string {
  return at.split('T')[1] ?? at;
}

function isSyn(provenance: string): boolean {
  return provenance.startsWith('[syn]');
}

const OPEN_CASE_STATES = new Set([
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
]);

function openCaseForVariable(variableId: string, cases: readonly Case[]): Case | undefined {
  return cases.find((c) => {
    if (!OPEN_CASE_STATES.has(c.state)) return false;
    const eventId = c.variationEventId;
    if (!eventId) return false;
    if (eventId.startsWith('ve-')) {
      const event = VARIATION_EVENTS.find((row) => row.id === eventId);
      return event?.variableId === variableId;
    }
    if (eventId.startsWith('ex-')) {
      return (
        eventId
          .slice(3)
          .replace(/-(six-point-trend|beyond-limit|widening-variability|cyclical)$/, '') ===
        variableId
      );
    }
    return false;
  });
}

function workbookTooltipForVariable(
  variableId: string,
  cases: readonly Case[],
  diagnosis: ReturnType<typeof diagnose>,
): string | undefined {
  const caseItem = cases.find((row) => {
    if (!OPEN_CASE_STATES.has(row.state)) return false;
    const eventId = row.variationEventId;
    if (!eventId) return false;
    if (eventId.startsWith('ve-')) {
      const event = VARIATION_EVENTS.find((row) => row.id === eventId);
      return event?.variableId === variableId;
    }
    if (eventId.startsWith('ex-')) {
      return (
        eventId
          .slice(3)
          .replace(/-(six-point-trend|beyond-limit|widening-variability|cyclical)$/, '') ===
        variableId
      );
    }
    return false;
  });
  if (!caseItem) return undefined;
  const ruleId = matchedRuleIdForCase(caseItem, diagnosis);
  if (!ruleId) return undefined;
  const causeIds = ruleById(ruleId)?.sourceCauseId;
  if (!causeIds || causeIds.length === 0) return undefined;
  return workbookTooltipForCauseIds(causeIds);
}

/** The Control Plan's closed band for a reading (by reading id, else by the
 *  instrument tag the plan names — `cp-zn-ph` is written against AT-150). */
function controlPlanBand(readingId: string, tag: string | undefined): { low: number; high: number } | undefined {
  const row = CONTROL_PLAN_ZN_ROWS.find(
    (candidate) => candidate.readingId === readingId || (tag !== undefined && candidate.instrumentTag === tag),
  );
  const { lo, hi } = row?.specOverlay ?? {};
  return lo !== undefined && hi !== undefined ? { low: lo, high: hi } : undefined;
}

function formatBandEdge(value: number): string {
  return Number.isInteger(value) ? value.toLocaleString('en-US') : value.toFixed(1);
}

function excursionDetectedAt(variableId: string, state: SessionState): string | undefined {
  const read = spcRead(variableId, state);
  const excursion = read.events.find((event) => event.variableId === variableId);
  return excursion?.detectedAt;
}

function ladderCell(
  readingId: string,
  state: SessionState,
  slug: string,
  cases: readonly Case[],
  diagnosis: ReturnType<typeof diagnose>,
  window?: ShiftWindow,
): LadderCell | undefined {
  const variable = MONITORED_VARIABLES.find((v) => v.readingId === readingId);
  const reading = readingByIdLookup(readingId);
  if (!variable || !reading) return undefined;

  const value = valueAt(readingId, state);
  const nodeId = nodeIdForReading(readingId);
  const syn = isSyn(reading.provenance);

  if (value === undefined) {
    return {
      readingId,
      variableId: variable.id,
      label: reading.label,
      value: 'no series at this seed',
      raw: 0,
      unit: '',
      bandPosition: 'n/a',
      cell: 'no-series',
      nodeHref: nodeHref(slug, nodeId, constraintDeepLinkReading(readingId)),
      syn,
    };
  }

  const read = spcRead(variable.id, state);
  const cell: SignalCell | 'not-computed' = read.signal?.cell ?? 'not-computed';
  const basisRead = readingComparisonBasis(reading);
  const planBand = basisRead ? undefined : controlPlanBand(readingId, reading.tag);
  const band =
    planBand ??
    (reading.band?.lo !== undefined && reading.band?.hi !== undefined
      ? { low: reading.band.lo, high: reading.band.hi }
      : undefined);
  const openCase = openCaseForVariable(variable.id, cases);
  const excursion = read.events.find((e) => e.variableId === variable.id);
  const sinceInWindow =
    excursion &&
    cell !== 'quiet' &&
    cell !== 'not-computed' &&
    (!window || windowContains(excursion.detectedAt, state.cursor, window));

  return {
    readingId,
    variableId: variable.id,
    label: reading.label,
    value: formatSpcValue(reading, value),
    raw: value,
    unit: reading.unit,
    bandPosition: valueBandPosition(value, reading.band),
    band,
    basis: basisRead ? { label: basisRead.label, value: basisRead.value } : undefined,
    bandLabel: !basisRead && band ? `${formatBandEdge(band.low)}–${formatBandEdge(band.high)}` : undefined,
    toleranceHalfWidth:
      reading.spec?.lo !== undefined && reading.spec?.hi !== undefined
        ? (reading.spec.hi - reading.spec.lo) / 2
        : undefined,
    cell,
    since: sinceInWindow ? raisedAtClock(excursion!.detectedAt) : undefined,
    caseId: openCase?.id,
    caseLabel: openCase?.code ?? openCase?.id,
    nodeHref: nodeHref(slug, nodeId, constraintDeepLinkReading(readingId)),
    syn,
    workbookTooltip: workbookTooltipForVariable(variable.id, cases, diagnosis),
  };
}

function applyDivergence(
  cell: LadderCell | undefined,
  divergenceByReading: ReadonlyMap<string, SampleDivergenceChip>,
): LadderCell | undefined {
  if (!cell) return cell;
  const chip = divergenceByReading.get(cell.readingId);
  return chip ? { ...cell, sampleDivergence: chip } : cell;
}

function cellSeverity(cell: LadderCell['cell']): number {
  if (cell === 'no-series') return -1;
  return CELL_RANK[cell];
}

function zScore(cellRow: LadderCell, state: SessionState): number {
  const read = spcRead(cellRow.variableId, state);
  if (!read.limits) return 0;
  const value = valueAt(cellRow.readingId, state);
  if (value === undefined) return 0;
  const sigma = read.limits.sigma;
  if (!sigma) return 0;
  return Math.abs((value - read.limits.mean) / sigma);
}

function sortRows(rows: LadderCell[], state: SessionState): LadderCell[] {
  return [...rows].sort((a, b) => {
    const sevDiff = cellSeverity(b.cell) - cellSeverity(a.cell);
    if (sevDiff !== 0) return sevDiff;
    const zDiff = zScore(b, state) - zScore(a, state);
    if (zDiff !== 0) return zDiff;
    return 0; // Array#sort is stable — seed order (MONITORED_VARIABLES) is preserved
  });
}

function worstCell(rows: readonly LadderCell[]): SignalCell | 'not-computed' {
  let worst: SignalCell | 'not-computed' = 'quiet';
  let rank = -1;
  for (const row of rows) {
    if (row.cell === 'no-series') continue;
    const score = CELL_RANK[row.cell];
    if (score > rank) {
      rank = score;
      worst = row.cell;
    }
  }
  return worst;
}

function earliestSince(rows: readonly LadderCell[]): string | undefined {
  const withSince = rows.map((r) => r.since).filter((s): s is string => Boolean(s));
  if (withSince.length === 0) return undefined;
  return [...withSince].sort()[0];
}

const ROWS_FIRST_PAINT = 3;

/** The stage-ladder projection: five stages in plant order, each row's cell
 * from `spcRead`, never authored. `opts.expanded` names stages currently
 * showing all their rows (UI state lives in the component; this stays pure). */
function rowListed(cell: LadderCell, state: SessionState, window?: ShiftWindow): boolean {
  if (!window) return true;
  if (cell.cell === 'quiet' || cell.cell === 'no-series' || cell.cell === 'not-computed') {
    return false;
  }
  const detectedAt = excursionDetectedAt(cell.variableId, state);
  return Boolean(detectedAt && windowContains(detectedAt, state.cursor, window));
}

export function stageLadder(
  state: SessionState,
  slug: string,
  opts?: { expanded?: ReadonlySet<StageId>; window?: ShiftWindow; compact?: boolean },
  advisory?: AdvisorySessionState,
): StageLadderModel {
  const cases = applyCases(CASES, state, INITIAL_ADVISORY_STATE);
  const diagnosis = diagnose(state, advisory ?? INITIAL_ADVISORY_STATE);
  const expanded = opts?.expanded ?? new Set<StageId>();
  const compact = opts?.compact ?? false;
  const window = opts?.window;
  const divergenceByReading = new Map(
    inBandDivergences(state).map((d) => {
      const request = advisory?.sampleRequests.get(d.id);
      const chip: SampleDivergenceChip = {
        direction: d.direction,
        requested: Boolean(request),
        requestedAt: request?.requestedAt.split('T')[1],
        samplesHref: `/projects/${slug}/quality?tab=samples-due`,
      };
      return [d.readingId, chip] as const;
    }),
  );

  const membership = new Map<StageId, string[]>(STAGES.map((s) => [s.id, [] as string[]]));
  const unfitted: string[] = [];
  const metSubprocesses = new Set(['sp-grind', 'sp-pb', 'sp-zn']);

  for (const tag of TAG_MAPPING) {
    const stageId = stageForReading(tag.readingId);
    if (stageId) {
      membership.get(stageId)!.push(tag.readingId);
    } else if (metSubprocesses.has(tag.subprocessId)) {
      unfitted.push(tag.readingId);
    }
  }

  const stages: StageRow[] = STAGES.map((stage) => {
    const readingIds = membership.get(stage.id) ?? [];
    const cells = readingIds
      .map((id) => ladderCell(id, state, slug, cases, diagnosis, window))
      .filter((c): c is LadderCell => c !== undefined)
      .map((c) => (divergenceByReading.has(c.readingId) ? { ...c, sampleDivergence: divergenceByReading.get(c.readingId) } : c));
    const sorted = sortRows(cells, state);
    const listed = window ? sorted.filter((cell) => rowListed(cell, state, window)) : sorted;
    const stageState = worstCell(sorted);
    const isExpanded = expanded.has(stage.id);
    // MS-N14: a stage whose only member row IS its own outcome reading
    // already says everything on the header line — showing the identical
    // row again underneath is a duplicate, not a disclosure. Render the
    // header alone; nothing is hidden because there was never a second row.
    // The stage itself stays on the ladder (it has a member) — only stages
    // with no member readings at this seed are dropped below.
    const isSoleOutcomeRow = cells.length === 1 && cells[0].readingId === stage.outcomeReadingId;
    // Rule of three (Q4): a stage whose rows are ALL quiet collapses to its
    // header line ("N in band") rather than showing its top three quiet
    // rows — there is nothing to triage. A stage with any non-quiet row
    // shows its worst three first paint, `N more` behind the click.
    // Ruling C: the window scopes listed events (since chips, stage since,
    // auto-expand) — never which member rows exist or how hidden is counted.
    const hasListedEvents = window ? listed.length > 0 : true;
    const collapsedCount = !hasListedEvents ? 0 : stageState === 'quiet' ? 0 : ROWS_FIRST_PAINT;
    const sourceRows = sorted;
    const quietHiddenCount = sorted.filter(
      (cell) => cell.cell === 'quiet' || cell.cell === 'no-series',
    ).length;
    let visible: LadderCell[];
    let hidden: number;
    if (isSoleOutcomeRow) {
      visible = [];
      hidden = 0;
    } else if (compact && !isExpanded) {
      // Compact first paint: header-only stages; every non-duplicate row sits
      // behind the expand control (`hidden` counts against zero visible rows).
      visible = [];
      hidden = !hasListedEvents ? quietHiddenCount : sourceRows.length;
    } else if (isExpanded) {
      visible = sourceRows;
      hidden = 0;
    } else if (!hasListedEvents) {
      visible = [];
      hidden = quietHiddenCount;
    } else {
      visible = sourceRows.slice(0, collapsedCount);
      hidden = sourceRows.length - visible.length;
    }

    // SA-F12 fix round 1: when the divergent reading IS the stage outcome
    // reading AND it is also visible as a member row (not sole-outcome, not
    // collapsed away), the header chip would duplicate the member row's
    // identical chip. Suppress it there -- the header chip earns its place
    // only when the stage is collapsed and the member row is not on screen.
    const outcomeVisibleAsMember = visible.some((c) => c.readingId === stage.outcomeReadingId);
    const outcomeBase = ladderCell(stage.outcomeReadingId, state, slug, cases, diagnosis, window);
    const outcome =
      (outcomeVisibleAsMember ? outcomeBase : applyDivergence(outcomeBase, divergenceByReading)) ??
      ({
        readingId: stage.outcomeReadingId,
        variableId: stage.outcomeReadingId,
        label: stage.label,
        value: 'no series at this seed',
        raw: 0,
        unit: '',
        bandPosition: 'n/a' as const,
        cell: 'no-series' as const,
        nodeHref: nodeHref(slug, stage.subprocessId),
        syn: false,
      } satisfies LadderCell);

    const synRows = cells.filter((c) => c.syn);

    // MS-F20: the header must land on a unit op that resolves to the
    // stage's own subprocess, not just the first authored unit op -- Pb
    // regrind & cleaner's first unit op (RG01) is seeded under Grinding's
    // area, so `stage.unitOps[0]` alone sent the reader to the wrong
    // circuit. Fall back to the stage's own subprocess id when no unit op
    // resolves to it.
    const headerNode =
      stage.unitOps.find((u) => subprocessForNodeId(u) === stage.subprocessId) ?? stage.subprocessId;

    return {
      id: stage.id,
      label: stage.label,
      state: stageState,
      outcome,
      rows: visible,
      hidden,
      soleOutcome: isSoleOutcomeRow,
      synCount: synRows.length,
      synTotal: cells.length,
      nodeHref: nodeHref(slug, headerNode, windowReadingForStage(stage)),
      since: earliestSince(listed),
    };
  }).filter((stage) => stage.synTotal > 0);

  return { stages, unfitted };
}

// --- Control-room stage read (cro-brief R3, #267) ---------------------------

export type LeverChip = {
  role: string;
  tag: string;
  label: string;
  value: number;
  setpoint: number;
  unit: string;
  tone: VizTone;
  readingId: string;
  title: string;
};

export type StageReadRow = {
  stageId: 'pb-rougher' | 'pb-cleaner' | 'zn-rougher' | 'zn-cleaner';
  label: string;
  grade: string;
  levers: readonly LeverChip[];
};

/** D-aero: TAG_MAPPING files Aero 5100 under sp-pb; the Met tile and CRO
 *  stage read place the collector on Zn rougher (vision-run decisions.md). */
const LEVER_STAGE_OVERRIDES: Record<string, StageReadRow['stageId']> = {
  'reagent-aero-rate': 'zn-rougher',
};

const CRO_STAGE_IDS: readonly StageReadRow['stageId'][] = [
  'pb-rougher',
  'pb-cleaner',
  'zn-rougher',
  'zn-cleaner',
];

const STAGE_GRADE_READING: Record<StageReadRow['stageId'], string> = {
  'pb-rougher': 'rd-pb-rgh-con-pb',
  'pb-cleaner': 'rd-kpi-pb-grade',
  'zn-rougher': 'rd-zn-rgh-con-zn',
  'zn-cleaner': 'rd-kpi-zn-grade',
};

const STAGE_LEVER_ROLES: Record<StageReadRow['stageId'], readonly string[]> = {
  'pb-rougher': ['pb-rougher-air', 'pb-rougher-froth', 'reagent-3418a-rate'],
  'pb-cleaner': ['reagent-smbs-rate'],
  'zn-rougher': ['zn-ph-loop', 'zn-rougher-air', 'zn-rougher-froth', 'reagent-aero-rate'],
  'zn-cleaner': ['reagent-cuso4-rate'],
};

function leverTone(value: number, setpoint: number): VizTone {
  if (setpoint <= 0) return 'neutral';
  const deltaPct = ((value - setpoint) / setpoint) * 100;
  if (Math.abs(deltaPct) <= 5) return 'ok';
  if (Math.abs(deltaPct) <= 10) return 'warn';
  return 'bad';
}

function formatLeverUnit(unit: string): string {
  if (unit === 'm3/min') return 'm³/min';
  if (unit === 'g/t') return 'g/t';
  return unit;
}

function leverChipFace(
  reading: { unit: string; decimals?: number },
  tag: string,
  shortLabel: string,
  value: number,
  setpoint: number,
  onSetpoint: boolean,
  options?: { reagent?: boolean },
): string {
  const unit = formatLeverUnit(reading.unit);
  const valueStr =
    reading.unit === 'pH'
      ? value.toFixed(1)
      : reading.unit === '%' || reading.unit === 'g/t'
        ? formatSpcValue(reading as Parameters<typeof formatSpcValue>[0], value)
        : String(Math.round(value));
  const spSuffix = onSetpoint ? '· at SP' : `· SP ${reading.unit === 'pH' ? setpoint.toFixed(1) : Math.round(setpoint)}`;
  const unitSuffix = unit && reading.unit !== 'pH' && reading.unit !== '%' ? ` ${unit}` : '';
  const prefix = options?.reagent ? shortLabel : `${tag} ${shortLabel}`;
  return `${prefix} ${valueStr}${unitSuffix} ${spSuffix}`;
}

function leverFromReading(
  readingId: string,
  role: string,
  tag: string,
  shortLabel: string,
  state: SessionState,
  options?: { useReadingLabel?: boolean },
): LeverChip | undefined {
  const reading = readingByIdLookup(readingId);
  if (!reading) return undefined;
  const value = valueAt(readingId, state) ?? reading.value ?? 0;
  const setpoint =
    reading.design ??
    (reading.band?.lo !== undefined && reading.band?.hi !== undefined
      ? (reading.band.lo + reading.band.hi) / 2
      : reading.target ?? value);
  const deltaPct = setpoint > 0 ? ((value - setpoint) / setpoint) * 100 : 0;
  const onSetpoint = Math.abs(deltaPct) <= 5;
  const faceLabel = options?.useReadingLabel ? reading.label : shortLabel;
  const chipLabel = leverChipFace(reading, tag, faceLabel, value, setpoint, onSetpoint, {
    reagent: options?.useReadingLabel,
  });
  const unit = reading.unit === 'g/t' ? '' : reading.unit;
  return {
    role,
    tag,
    label: chipLabel,
    value,
    setpoint,
    unit,
    tone: leverTone(value, setpoint),
    readingId,
    title: `${tag} · ${reading.label} · ${formatSpcValue(reading, value)} vs SP ${formatSpcValue(reading, setpoint)}`,
  };
}

function znPhLever(state: SessionState): LeverChip {
  const loop = CONTROL_LOOPS.find((row) => row.tag === 'AIC5002');
  const reading = readingByIdLookup('rd-kpv-zn-ph')!;
  const value = valueAt('rd-kpv-zn-ph', state) ?? reading.value ?? 0;
  const setpoint =
    reading.band?.hi !== undefined && reading.band?.lo !== undefined
      ? (reading.band.lo + reading.band.hi) / 2
      : 11.1;
  const deltaPct = setpoint > 0 ? ((value - setpoint) / setpoint) * 100 : 0;
  const atSp = Math.abs(deltaPct) <= 5;
  return {
    role: 'zn-ph-loop',
    tag: loop?.tag ?? 'AIC5002',
    label: leverChipFace(reading, loop?.tag ?? 'AIC5002', 'lime pH', value, setpoint, atSp),
    value,
    setpoint,
    unit: 'pH',
    tone: leverTone(value, setpoint),
    readingId: 'rd-kpv-zn-ph',
    title: `${loop?.tag ?? 'AIC5002'} · Zn float pH · ${value.toFixed(1)} vs SP ${setpoint.toFixed(1)}`,
  };
}

function leversForStage(stageId: StageReadRow['stageId'], state: SessionState): LeverChip[] {
  const roles = STAGE_LEVER_ROLES[stageId];
  const chips: LeverChip[] = [];

  if (stageId === 'zn-rougher') {
    chips.push(znPhLever(state));
  }

  for (const role of roles) {
    if (role === 'zn-ph-loop') continue;
    const stageOverride = LEVER_STAGE_OVERRIDES[role];
    if (stageOverride && stageOverride !== stageId) continue;
    const mappedStage = stageOverride ?? stageId;
    if (mappedStage !== stageId) continue;

    if (role === 'reagent-aero-rate') {
      const chip = leverFromReading('rd-reag-aero', role, 'Aero5100', 'collector', state, {
        useReadingLabel: true,
      });
      if (chip) chips.push(chip);
      continue;
    }

    const tagRow = TAG_MAPPING.find((row) => row.role === role);
    if (!tagRow) continue;
    const isReagent = role.startsWith('reagent-');
    const shortLabel = role.includes('air')
      ? 'air'
      : role.includes('froth')
        ? 'froth'
        : role.replace('reagent-', '').replace('-rate', '');
    const chip = leverFromReading(
      tagRow.readingId,
      role,
      tagRow.instrumentTag,
      shortLabel,
      state,
      isReagent ? { useReadingLabel: true } : undefined,
    );
    if (chip) chips.push(chip);
  }

  const cap = stageId === 'zn-rougher' ? 4 : 3;
  return chips.slice(0, cap);
}

function formatPct(readingId: string, state: SessionState): string {
  const reading = readingByIdLookup(readingId);
  const value = valueAt(readingId, state) ?? reading?.value;
  if (value === undefined || !reading) return '—';
  return `${formatSpcValue(reading, value)}%`;
}

const STAGE_LABEL: Record<StageReadRow['stageId'], string> = {
  'pb-rougher': 'Pb rougher',
  'pb-cleaner': 'Pb cleaner',
  'zn-rougher': 'Zn rougher',
  'zn-cleaner': 'Zn cleaner',
};

export function croStageRead(state: SessionState, _window?: ShiftWindow): readonly StageReadRow[] {
  return CRO_STAGE_IDS.map((stageId) => ({
    stageId,
    label: STAGE_LABEL[stageId],
    grade: formatPct(STAGE_GRADE_READING[stageId], state),
    levers: leversForStage(stageId, state),
  }));
}
